// Generated by tutorials/record.cjs from tutorials/articles/h35-nutritional-analysis-and-validation.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h35 --text-only` (wording) or
// `node tutorials/record.cjs h35` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'nutritional-analysis-and-validation',
  catalogId: 'h35',
  title: 'Nutritional analysis and validation',
  summary: 'Check the nutrition a menu gives each diet order, or one resident, against the targets, and find the menu items whose missing recipe data throws the numbers off.',
  audience: 'Dietitians and dietary managers who review menus and residents\' nutrition',
  area: 'clinical',
  learn: [
    'Check one meal or a full day for a diet order against its targets',
    'Check a week or the whole menu cycle and find the days off target',
    'Check one resident\'s day against their own needs',
    'Choose the nutrients, and what the report does with more than 12',
    'Find menu items with missing nutrient data, and fix them',
    'What to do with a finding',
  ],
  keywords: [
    'nutritional analysis',
    'nutrition analysis',
    'nutrient analysis',
    'menu nutrition',
    'nutrients',
    'calories',
    'protein',
    'sodium',
    'fiber',
    'dri',
    'dri group',
    'targets',
    'under target',
    'over target',
    'full day',
    'week',
    'whole cycle',
    'diet order',
    'resident analysis',
    'needs',
    'validation',
    'nutritional validation',
    'missing nutrient data',
    'not linked',
    'make it a recipe',
    'nutrition - analysis report',
    'too many nutrients',
    '12 nutrients',
  ],
  steps: [
    {
      id: 'open',
      part: 'Check a meal against its targets',
      heading: 'Open Nutritional Analysis',
      text: 'Under Clinical, select Nutritional Analysis.\n\nThe tabs across the top are the ways to look: Meal, Week, Compare, Resident and Intake Study. The page opens on Meal, with your active menu cycle, Day 1 and Breakfast.\n\nEvery number comes from the items\' recipes, for the portion each diet order is served, the way the tray ticket serves it.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/open.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Nutritional Analysis on the Meal tab: Menu Cycle Fall/Winter Cycle 2026, Day 1, Breakfast, House diet, DRI Group Female 71+, with the nutrient tiles below.',
      },
    },
    {
      id: 'choose',
      part: 'Check a meal against its targets',
      heading: 'Choose the meal and the diet order',
      text: 'Pick the Menu Cycle, the Day and the Mealtime.\n\nThen the diet order: a Diet Type, and a Consistency if you want one. House diet is the menu as written, with no diet type.\n\nThe DRI Group sets the targets the meal is checked against. It starts on your facility\'s default.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/choose.webp',
          width: 1280,
          height: 204,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/choose.mp4',
          width: 1280,
          height: 204,
          durationSec: 6,
        },
        alt: 'The choices set to Day 1, Lunch, Diet Type Cardiac, Consistency None and DRI Group Female 71+.',
      },
    },
    {
      id: 'tiles',
      part: 'Check a meal against its targets',
      heading: 'Read the totals against the targets',
      text: 'Each tile is one nutrient: the meal\'s total, then Under, Within or Over against the DRI group\'s target for this meal, with the target beside it. The last line is the share of the group\'s value for a whole day.\n\nThis cardiac lunch is within its targets for calories, protein, carbohydrate and fat, over on sodium, and under on fiber.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/tiles.webp',
          width: 1280,
          height: 196,
        },
        alt: 'Six tiles: Calories 639 Within 600 - 733, Protein 34.0 Within, Carbohydrate Within, Total Fat Within, Sodium 824 Over at most 767, Dietary Fiber 5.7 Under at least 7.0.',
      },
    },
    {
      id: 'chart',
      part: 'Check a meal against its targets',
      heading: 'See it as a chart',
      text: 'Nutrients vs Target draws the same totals: the shaded band is the target range, with dashed lines at its ends, and the bar is blue when the total is under, green within and red over.\n\nCalories by Macronutrient shows how the meal\'s calories split between protein, carbohydrate and fat.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/chart.webp',
          width: 1280,
          height: 380,
        },
        alt: 'The Nutrients vs Target chart with a red Sodium bar past its target and a blue Dietary Fiber bar short of it, and the Calories by Macronutrient ring showing 639 kcal.',
      },
    },
    {
      id: 'items',
      part: 'Check a meal against its targets',
      heading: 'Check the items, and the ones not counted',
      text: 'Below the charts are the items, each with its nutrients for the portion served. A component, such as gravy on the chicken, sits under its item.\n\nThe yellow note above the tiles means some items are not counted, or only partly. Select Show which items and why: here Cranberry Relish has no recipe, so it adds nothing, and Peach Crisp has an ingredient with no nutrient values. The totals read low until those are fixed in Food Data. Validation, below, lists every one on the menu.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/items.webp',
          width: 1280,
          height: 450,
        },
        alt: 'The item list for the cardiac lunch, Baked Chicken Breast with Chicken Gravy under it, with a warning sign on Cranberry Relish and a partly counted sign on Peach Crisp, and the totals row.',
      },
    },
    {
      id: 'full-day',
      part: 'Check a meal against its targets',
      heading: 'Add up a full day, or check the other choices',
      text: 'Full Day adds breakfast, lunch and dinner together, against the three meals\' targets added up.\n\nFirst choice is what the tray ticket serves. Alternate checks the second choice in each category, and Always Served the menu offered at every meal.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/full-day.webp',
          width: 1280,
          height: 854,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/full-day.mp4',
          width: 1280,
          height: 854,
          durationSec: 3.6,
        },
        alt: 'Day 1 Full Day totals for the Cardiac diet: Calories 1,895 Within 1,800 - 2,200, Sodium Over at most 2,300, Dietary Fiber Under at least 21.0.',
      },
    },
    {
      id: 'week',
      part: 'Check a week or the whole cycle',
      heading: 'Analyse a week',
      text: 'Select the Week tab. It checks every day of a week, or of the whole cycle, for one diet order.\n\nPick the Days: a week, with its dates in the rotation running now, or Whole cycle. Then select Analyse (here Analyse Cardiac). Each day is built meal by meal as the tray ticket builds it, about a minute a week, and the days fill in as they are done.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/week.webp',
          width: 1280,
          height: 612,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/week.mp4',
          width: 1280,
          height: 612,
          durationSec: 6,
        },
        alt: 'The Week tab with Days set to Week 1 (days 1-7) and the Analyse Cardiac button, the table filling in day by day.',
      },
    },
    {
      id: 'days',
      part: 'Check a week or the whole cycle',
      heading: 'Find the days off target',
      text: 'One row per nutrient, one column per day. A blue cell is under the daily target and a red one over; grey means a meal could not be built.\n\nAt the end of each row: the daily average (full days only), its share of the DRI group\'s daily value, and the daily target. Here the cardiac diet is over on sodium every day and under on fiber on most days.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/days.webp',
          width: 1280,
          height: 330,
        },
        alt: 'Cardiac - daily totals for days 1 to 7: Sodium red on every day with a daily average of 2,624 mg against at most 2,300, Dietary Fiber blue on six of the seven days.',
      },
    },
    {
      id: 'no-data',
      part: 'Check a week or the whole cycle',
      heading: 'See which items are not counted',
      text: 'Under the table: the items with no nutrient data, with the days they are served on, and the items only partly counted, with why. The totals for those days read low until the item or its recipe is fixed in Food Data.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/no-data.webp',
          width: 1280,
          height: 356,
        },
        alt: 'One item has no nutrient data: Cranberry Relish, on days 1 to 7, not linked to a recipe. Two items are only partly counted: Beef Stew and Peach Crisp, with the reasons.',
      },
    },
    {
      id: 'finding',
      part: 'Check a week or the whole cycle',
      heading: 'What to do with a finding',
      text: 'When a total is off target:\n\n1. Check the items not counted first. A missing recipe or an ingredient with no values makes a total look low when the menu is fine. Fix those in Food Data (Validation lists them all).\n2. If the menu itself is off, for example sodium over on most days, ask for a menu change, or talk it over with whoever writes your menus. See Asking for a menu change.\n3. For one resident, check their own day on the Resident tab: their meal pattern, snacks and supplements are what you can change for them.',
      textOnly: true,
    },
    {
      id: 'resident',
      part: 'Check one resident\'s day',
      heading: 'Pick a resident',
      text: 'Select the Resident tab. It checks one resident\'s own menu as their tray tickets build it: their diet order, adjustments such as dislikes and allergies, their meal pattern and their nourishments.\n\nPick the Resident and the Date. Day checks that date; Week the seven days from it (that takes a few minutes).',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/resident.webp',
          width: 1280,
          height: 750,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/resident.mp4',
          width: 1280,
          height: 750,
          durationSec: 6,
        },
        alt: 'The Resident tab with Carvalho, Ines (116-B) picked, today\'s date, and Day selected.',
      },
    },
    {
      id: 'needs',
      part: 'Check one resident\'s day',
      heading: 'Compare the day with the resident\'s needs',
      text: 'At the top: the resident\'s diet order, the menu day, the DRI group, and their needs per day from their Calorie/Protein/Fluids section.\n\nThe Calories and Protein tiles (and Fluids, when it is one of the nutrients) compare the whole day with those needs. The other tiles show the share of the DRI group\'s daily value.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/needs.webp',
          width: 1280,
          height: 792,
        },
        alt: 'Carvalho, Ines: Regular, Pureed, Mildly Thick Liquids; needs per day Calories 2,000 - 2,300 kcal, Protein 80 - 100 g, Fluids 1,500 - 1,700 mL; the Calories and Protein tiles Within.',
      },
    },
    {
      id: 'by-meal',
      part: 'Check one resident\'s day',
      heading: 'See each meal and snack',
      text: 'By meal and nourishment splits the day into each meal and each snack (here PM Snack and HS Snack), with the day\'s total at the bottom. The list below it has every item the resident is served, with its nutrients.\n\nPrint report opens the Nutrition - Analysis report for this resident and date.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/by-meal.webp',
          width: 1280,
          height: 362,
        },
        alt: 'By meal and nourishment: Breakfast, Lunch, Dinner, PM Snack and HS Snack, with the Day total row.',
      },
    },
    {
      id: 'nutrients',
      part: 'Choose nutrients and print',
      heading: 'Choose the nutrients',
      text: 'The page starts with your facility\'s usual nutrients. Select Nutrients (the number is how many are on) to see them all, and select one to add or remove it. A blue nutrient is on.\n\nEvery tab uses the same list.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/nutrients.webp',
          width: 1280,
          height: 148,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/nutrients.mp4',
          width: 1280,
          height: 148,
          durationSec: 6,
        },
        alt: 'The Nutrients list open with 13 on: Calories, Protein, Carbohydrate, Total Fat, Sodium, Dietary Fiber, Fluids, Calcium, Potassium, Saturated Fat, Cholesterol, Total Sugars and Vitamin C.',
      },
    },
    {
      id: 'too-many',
      part: 'Choose nutrients and print',
      heading: 'Print, with no more than 12 nutrients',
      text: 'Print report opens the Nutrition - Analysis report with the page\'s choices filled in, ready to generate, print or save as PDF. It is on the Meal, Week and Resident tabs.\n\nThe printed report shows 12 nutrients at most. With more than 12 on, a warning, Too many nutrients for the report, names the ones it will leave out (the last ones in the list). Select Open report to go on without them, or Cancel and take some off first.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/too-many.webp',
          width: 708,
          height: 406,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/too-many.mp4',
          width: 708,
          height: 406,
          durationSec: 2,
        },
        alt: 'The Too many nutrients for the report box: the printed report shows up to 12 nutrients, you picked 13, so it will leave out Vitamin C. Cancel and Open report.',
      },
    },
    {
      id: 'report',
      part: 'Choose nutrients and print',
      heading: 'Where the report goes',
      text: 'The report opens in Reports with your choices filled in. Select Generate, and it goes to your Print Queue like any other report. From the Week tab, Print report covers the week you picked (or the first 30 days of the whole cycle); from the Resident tab, that resident\'s day or week.',
      textOnly: true,
    },
    {
      id: 'validation',
      part: 'Find menu items with missing nutrition',
      heading: 'Open Validation',
      text: 'Under Clinical, select Validation. Menu Items lists the items on the menu cycle in use (named at the top) whose nutrition cannot be counted properly: an item not linked to a recipe, an ingredient or a tube feed product, or one served in a size its recipe does not have.\n\nShow Warnings Only is on, so only the items with a problem show. A yellow sign marks each one; point at it to read the warning.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/validation.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/validation.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'Validation, Menu Items: Showing Items for Fall/Winter Cycle 2026. Chicken Gravy with warnings on its 4 oz and 1/4 c sizes, Cranberry Relish (Not Linked) with a warning, and Health Shake with a warning on its 8 oz size.',
      },
    },
    {
      id: 'sizes',
      part: 'Find menu items with missing nutrition',
      heading: 'Check the sizes',
      text: 'An item with a size problem opens to show its sizes. The warning says the size is not set up on the recipe (or product) the item is linked to, so a serving of that size cannot be counted.\n\nSelect Highlight Sizes On Menu: the sizes the menu really serves turn green. Those are the ones to fix first.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/sizes.webp',
          width: 1280,
          height: 810,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/sizes.mp4',
          width: 1280,
          height: 810,
          durationSec: 3.9,
        },
        alt: 'Chicken Gravy\'s sizes 4 oz and 1/4 c with size warnings, the 1/4 c size highlighted green because the menu serves it.',
      },
    },
    {
      id: 'open-item',
      part: 'Find menu items with missing nutrition',
      heading: 'Open the item to fix it',
      text: 'Select the item and then Open, or double-click it. The item opens on its Nutrition section, where you link it to a recipe, or open its recipe to add the missing size. When you close it after saving, the list checks again. See Items and recipes.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/open-item.webp',
          width: 1280,
          height: 810,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/open-item.mp4',
          width: 1280,
          height: 810,
          durationSec: 3.5,
        },
        alt: 'Cranberry Relish selected in the Menu Items list, and the Open button at the top left.',
      },
    },
    {
      id: 'missing',
      part: 'Find menu items with missing nutrition',
      heading: 'See all the missing nutrient data',
      text: 'Select the Missing Nutrient Data tab. It lists every menu item, recipe and ingredient on your active menus that counts as zero or only partly in nutrient totals, and the menu items that still need a recipe. The most served are at the top; Times on menus says how often.\n\nStatus says what is wrong: Counts as zero, Partly counted, or Needs a recipe. Problem says why. Fix them in Food Data: add the recipe\'s ingredients and amounts, an ingredient\'s nutrient values, or a weight for a unit such as each or slice.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/missing.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/missing.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.7,
        },
        alt: 'Missing Nutrient Data: Of 86 menu items on 1 active menu, 1 counts as zero, 2 are only partly counted and 2 need a recipe. Rows for Cranberry Relish, Iced Tea, Dinner Roll, Peach Crisp, Crisp Topping Mix and Beef Stew.',
      },
    },
    {
      id: 'make-recipe',
      part: 'Find menu items with missing nutrition',
      heading: 'Make it a recipe',
      text: 'Every menu item should be a recipe. An item linked straight to a USDA food shows Needs a recipe. Tick those items (only they can be ticked) and select Make it a recipe, or Make 2 recipes for two. Then select Make recipes.\n\nEach item gets a recipe with one ingredient, its USDA food, and is linked to it, so its nutrients stay the same. An item served in a size with no weight, such as 1 slice, is left as it is until a weight is added in Food Data.',
      media: {
        image: {
          src: '/help/nutritional-analysis-and-validation/make-recipe.webp',
          width: 944,
          height: 422,
        },
        clip: {
          src: '/help/nutritional-analysis-and-validation/make-recipe.mp4',
          width: 944,
          height: 422,
          durationSec: 3.5,
        },
        alt: 'The Make it a recipe box after Make recipes: 2 recipes made, Iced Tea and Dinner Roll each with a new recipe number.',
      },
    },
  ],
  tips: [
    'Compare, the third tab, shows the same meal for up to six diet orders side by side, such as Regular, Cardiac and Renal, with the lowest and highest value for each nutrient marked.',
    'Recalculate works the meal out again after someone changes the menu or a recipe. Export CSV saves what is on screen as a spreadsheet file.',
    'The DRI Group starts on your facility\'s default. Pick another group to see the same menu against, for example, the targets for men over 70.',
    'The three small buttons next to Always Served show the charts and the item list together, the charts only, or the list only.',
  ],
  notes: [
    'Every number on these pages comes from the items\' recipes, for the portion each diet order is served, the way the tray ticket serves it. Nothing is typed in. An item with no recipe, or a recipe ingredient with no nutrient values, adds nothing, so the totals read low until it is fixed in Food Data.',
    'The targets are the DRI group\'s. A meal\'s target is the group\'s target for that meal; a full day adds the three meals\' targets. "% of daily DRI" compares the total with the group\'s value for a whole day.',
    'A resident\'s needs come from their Calorie/Protein/Fluids section. If a need is not set there, the tile says No target set.',
    'Nutritional Analysis needs the Analysis level on your role, and Validation the Nutritional Assessment level. Make it a recipe needs a Recipe level that can add recipes. If something is missing, ask your administrator.',
  ],
  related: [
    'menu-analysis',
    'items-and-recipes',
    'meal-patterns',
    'asking-for-a-menu-change',
    'intake-studies',
    'running-a-report',
    'favorites-and-print-queue',
  ],
  recorded: '2026-11-03',
};

export default article;
